import React, { useState } from 'react';
import JSZip from 'jszip';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { FileImage, Box, Download, RefreshCw, AlertCircle, FileText, Eye, File } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { usePropertyAllFiles, UnifiedFile } from '@/features/properties/hooks/usePropertyAllFiles';
import { getPublicUrl, downloadFromUrl } from '@/shared/lib/downloadFile';
import { useDownloadManager } from '@/features/exports/services/DownloadManagerContext';
import { fetchBytes } from '@/features/exports/services/bundles/shared';
import { sanitize } from '@/features/exports/services/_helpers';
import { toast } from 'sonner';

/** Display label per tab — used in the download manager and the zip filename. */
const TAB_LABELS: Record<string, string> = {
  drawings: 'Reports',
  documents: 'PDF Drawings',
  '2d-drawings': 'Drawings',
  '3d-files': 'Point Cloud',
  all: 'Existing Documents',
};

interface FileManagerProps {
  propertyId: string;
  className?: string;
  /** Rendered alongside the Refresh button (e.g. an Export button). */
  headerActions?: React.ReactNode;
}

function FileManagerSkeleton({ className = "" }: { className?: string }) {
  return (
    <div className={className}>
      <div className="mb-6 flex items-center justify-between gap-2">
        <div className="space-y-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-52" />
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      </div>

      <div className="space-y-8">
        {Array.from({ length: 5 }).map((_, sectionIndex) => (
          <div key={sectionIndex}>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-8" />
              </div>
              {sectionIndex < 2 && <Skeleton className="h-8 w-24 rounded-md" />}
            </div>

            {sectionIndex < 3 ? (
              <div className="grid gap-2">
                {Array.from({ length: sectionIndex === 0 ? 2 : 1 }).map((_, fileIndex) => (
                  <Card key={fileIndex} className="bg-card/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg" />
                          <div className="min-w-0 flex-1 space-y-2">
                            <Skeleton className="h-4 w-2/5" />
                            <Skeleton className="h-3 w-3/5" />
                          </div>
                        </div>
                        <div className="hidden items-center gap-2 sm:flex">
                          <Skeleton className="h-8 w-20 rounded-md" />
                          <Skeleton className="h-8 w-24 rounded-md" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/50 py-6">
                <Skeleton className="mx-auto h-3 w-40" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (filename: string, fileType?: string) => {
  if (fileType === '3d_model') return Box;
  if (fileType === 'point_cloud') return Box;
  
  const ext = filename.toLowerCase().split('.').pop();
  if (['rcs', 'pts', 'e57'].includes(ext || '')) return Box;
  if (ext === 'pdf') return FileText;
  if (['dwg', 'dxf'].includes(ext || '')) return FileImage;
  if (['glb', 'gltf', 'obj', 'fbx', 'ifc', 'rvt'].includes(ext || '')) return Box;
  if (['doc', 'docx', 'txt', 'csv', 'xlsx'].includes(ext || '')) return FileText;
  return File;
};

const isPDF = (filename: string) => {
  return filename.toLowerCase().endsWith('.pdf');
};

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic'];
const isImage = (filename: string) => {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  return IMAGE_EXTS.includes(ext);
};
const isViewable = (filename: string) => isPDF(filename) || isImage(filename);

const getCategoryBadge = (category: string, fileType?: string) => {
  const categoryMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
    'point_cloud': { label: 'Point Cloud', variant: 'default' },
    'point_cloud_archive': { label: 'Point Cloud Archive', variant: 'secondary' },
    'drawing': { label: 'Drawing', variant: 'outline' },
    'cad': { label: 'CAD', variant: 'outline' },
    'Drawing': { label: 'Drawing', variant: 'outline' },
    'document': { label: 'Document', variant: 'secondary' },
    'Document': { label: 'Document', variant: 'secondary' },
    '3d_model': { label: '3D Model', variant: 'default' },
    '3D Model': { label: '3D Model', variant: 'default' },
  };
  
  if (fileType === '3d_model') return { label: '3D Model', variant: 'default' as const };
  if (fileType === 'drawing') return { label: 'Drawing', variant: 'outline' as const };
  if (fileType === 'document') return { label: 'Document', variant: 'secondary' as const };
  
  return categoryMap[category] || { label: category, variant: 'secondary' as const };
};

export default function FileManager({
  propertyId,
  className = "",
  headerActions,
}: FileManagerProps) {
  const [previewFile, setPreviewFile] = useState<UnifiedFile | null>(null);
  const { files, allFiles, isLoading, error, refetch } = usePropertyAllFiles(propertyId);

  const handleOpen = (file: UnifiedFile) => {
    const url = getPublicUrl({ download_url: file.download_url, file_path: file.file_path });
    if (!url) {
      toast.error('No public link available');
      return;
    }
    setPreviewFile(file);
  };

  const previewUrl = previewFile
    ? getPublicUrl({ download_url: previewFile.download_url, file_path: previewFile.file_path })
    : null;

  const handleDownload = async (file: UnifiedFile) => {
    const url = getPublicUrl({ download_url: file.download_url, file_path: file.file_path });
    if (!url) {
      toast.error('No public link available');
      return;
    }
    try {
      await downloadFromUrl(url, file.filename);
      toast.success('Download started');
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error('Failed to download file');
    }
  };

  const { enqueue } = useDownloadManager();

  /** All sections to render — order matches the old tab order. */
  const sections = [
    { key: 'drawings', label: 'Reports', icon: FileImage, files: files.siteReports },
    { key: 'documents', label: 'PDF Drawings', icon: FileText, files: files.pdfDocuments },
    { key: '2d-drawings', label: 'Drawings', icon: FileImage, files: files.twoDFiles },
    { key: '3d-files', label: 'Point Cloud', icon: Box, files: files.threeDFiles },
    { key: 'all', label: 'Existing Documents', icon: File, files: files.existingDocs },
  ];

  if (isLoading) {
    return <FileManagerSkeleton className={className} />;
  }

  const handleDownloadSection = (sectionKey: string, sectionFiles: UnifiedFile[]) => {
    if (sectionFiles.length === 0) {
      toast.error('No files to download in this section');
      return;
    }
    const tabLabel = TAB_LABELS[sectionKey] ?? sectionKey;

    enqueue({
      label: `${tabLabel} bundle`,
      filename: `${sanitize(tabLabel)}.zip`,
      build: async (ctx) => {
        const zip = new JSZip();
        const seenNames = new Map<string, number>();
        let done = 0;
        let added = 0;
        let skipped = 0;

        for (const file of sectionFiles) {
          if (ctx.signal.aborted) break;
          const url = getPublicUrl({ download_url: file.download_url, file_path: file.file_path });
          if (!url) {
            skipped++;
          } else {
            const buf = await fetchBytes(url, { signal: ctx.signal });
            if (buf) {
              let name = file.filename || `file-${file.id}`;
              const prior = seenNames.get(name);
              if (prior !== undefined) {
                const next = prior + 1;
                seenNames.set(name, next);
                const dot = name.lastIndexOf('.');
                name = dot > 0 ? `${name.slice(0, dot)}-${next}${name.slice(dot)}` : `${name}-${next}`;
              } else {
                seenNames.set(name, 1);
              }
              zip.file(name, buf);
              added++;
            } else {
              skipped++;
            }
          }
          done++;
          ctx.setProgress(
            Math.floor((done / sectionFiles.length) * 90),
            `Downloading ${done} / ${sectionFiles.length}`,
          );
        }

        ctx.setProgress(95, 'Compressing…');
        const blob = await zip.generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: { level: 5 },
        });
        ctx.setProgress(100, `Added ${added} file(s)${skipped > 0 ? `, skipped ${skipped}` : ''}`);
        return blob;
      },
    });
  };

  const renderFileCard = (file: UnifiedFile, index: number) => {
    const FileIcon = getFileIcon(file.filename, file.file_type);
    const categoryBadge = getCategoryBadge(file.category, file.file_type);
    return (
      <Card key={index} className="hover:bg-accent/50 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
                <FileIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium truncate">{file.filename}</h4>
                <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                  {file.file_size && <span>{formatFileSize(file.file_size)}</span>}
                  <Badge variant={categoryBadge.variant} className="text-xs">
                    {categoryBadge.label}
                  </Badge>
                  {file.uploaded_at && (
                    <span className="text-xs">
                      {new Date(file.uploaded_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {getPublicUrl({ download_url: file.download_url, file_path: file.file_path }) && (
                <>
                  {isViewable(file.filename) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpen(file)}
                      className="gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(file)}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={className}>
      {/* Global header */}
      <div className="flex items-center justify-between gap-2 mb-6">
        <div>
          <h3 className="text-lg font-semibold">Property Files</h3>
          <p className="text-sm text-muted-foreground">
            {allFiles.length} file{allFiles.length !== 1 ? 's' : ''} across {sections.filter(s => s.files.length > 0).length} categories
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {headerActions}
          <Button
            variant="outline"
            size="sm"
            onClick={refetch}
            disabled={isLoading}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <div className="text-center py-12">
          <AlertCircle className="h-8 w-8 mx-auto mb-4 text-destructive" />
          <p className="text-destructive mb-4">Failed to load files</p>
          <Button variant="outline" onClick={refetch}>
            Try Again
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {sections.map(({ key, label, icon: SectionIcon, files: sectionFiles }) => (
            <div key={key}>
              {/* Section header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SectionIcon className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-semibold">{label}</h4>
                  <span className="text-xs text-muted-foreground">
                    ({sectionFiles.length})
                  </span>
                </div>
                {sectionFiles.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadSection(key, sectionFiles)}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                )}
              </div>

              {/* Section files */}
              {sectionFiles.length > 0 ? (
                <div className="grid gap-2">
                  {sectionFiles.map((file, index) => renderFileCard(file, index))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border/50 py-6 text-center">
                  <p className="text-xs text-muted-foreground">No {label.toLowerCase()} available</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!previewFile} onOpenChange={(open) => !open && setPreviewFile(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-4 pt-4 pb-2 border-b">
            <DialogTitle className="truncate text-base">
              {previewFile?.filename}
            </DialogTitle>
          </DialogHeader>
          {previewUrl && previewFile && (isPDF(previewFile.filename) ? (
            <iframe
              src={previewUrl}
              title={previewFile.filename}
              className="w-full flex-1 min-h-[70vh] border-0"
            />
          ) : (
            <div className="flex-1 min-h-[60vh] flex items-center justify-center bg-muted/30 overflow-auto p-4">
              <img
                src={previewUrl}
                alt={previewFile.filename}
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}